import React, { useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
} from '@floating-ui/react'
import DropdownMenu from './DropdownMenu'
import DropdownToggle from './DropdownToggle'
import PropTypes from 'prop-types'
import useUniqueId from '../hooks/useUniqueId'
import DropdownContext from './context/dropdownContext'
import DropdownMenuContext, {
    useDropdownMenuContext,
} from './context/dropdownMenuContext'
import chainedFunction from '../utils/chainedFunction'
import useRootClose from '../hooks/useRootClose'
import arrayIndexOf from '../utils/arrayIndexOf'
import { PLACEMENT } from '../utils/constant'

const CLICK = 'click'
const HOVER = 'hover'
const CONTEXT = 'context'

const Dropdown = React.forwardRef((props, ref) => {
    const {
        title,
        children,
        className,
        menuClass,
        menuStyle,
        disabled,
        renderTitle,
        placement,
        activeKey,
        toggleClassName,
        trigger,
        style,
        onClick,
        onMouseEnter,
        onMouseLeave,
        onContextMenu,
        onSelect,
        onOpen,
        onClose,
        onToggle,
        middleware,
        ...rest
    } = props

    const overlayTarget = useRef()
    const triggerTarget = useRef()
    const menuControl = useDropdownMenuContext(overlayTarget)
    const open = menuControl.open

    const buttonId = useUniqueId('dropdown-toggle-')
    const menuId = useUniqueId('base-menu-')

    // --- Floating UI Hook for Positioning ---
    const { x, y, strategy, refs } = useFloating({
        open,
        placement,
        whileElementsMounted: autoUpdate,
        middleware,
    })

    // Combine refs for Floating UI and local logic
    const setTriggerRef = useCallback(
        (node) => {
            triggerTarget.current = node
            refs.setReference(node)
        },
        [refs]
    )

    const setOverlayRef = useCallback(
        (node) => {
            overlayTarget.current = node
            refs.setFloating(node)
        },
        [refs]
    )
    // --- End Floating UI ---

    const handleToggle = useCallback(
        (isOpen) => {
            const nextOpen = typeof isOpen === 'undefined' ? !open : isOpen
            const fn = nextOpen ? onOpen : onClose

            fn?.()
            onToggle?.(nextOpen)
            if (nextOpen) {
                menuControl.openMenu()
            } else {
                menuControl.closeMenu()
            }
        },
        [onClose, onOpen, onToggle, open, menuControl]
    )

    const handleClick = useCallback(
        (e) => {
            e.preventDefault()
            if (disabled) {
                return
            }
            handleToggle()
        },
        [disabled, handleToggle]
    )

    const hoverTimeout = useRef(null)

    const handleMouseEnter = useCallback(() => {
        if (hoverTimeout.current) clearTimeout(hoverTimeout.current)
        if (!disabled) handleToggle(true)
    }, [disabled, handleToggle])

    const handleMouseLeave = useCallback(() => {
        if (hoverTimeout.current) clearTimeout(hoverTimeout.current)
        if (!disabled) {
            hoverTimeout.current = setTimeout(() => handleToggle(false), 100)
        }
    }, [disabled, handleToggle])

    // This is the function that closes the dropdown
    const handleSelect = (eventKey, e) => {
        onSelect?.(eventKey, e)
        handleToggle(false) // This closes the menu
    }

    // This hook handles closing the menu when clicking outside
    useRootClose(() => handleToggle(), {
        triggerTarget,
        overlayTarget,
        disabled: !open,
        listenEscape: true, // Also close on 'Escape' key
    })

    const dropdownProps = {
        onMouseEnter,
        onMouseLeave,
    }

    const toggleEventHandlers = {
        onClick: onClick,
        onContextMenu,
    }

    if (arrayIndexOf(CLICK, trigger)) {
        toggleEventHandlers.onClick = chainedFunction(
            handleClick,
            toggleEventHandlers.onClick
        )
    }

    if (arrayIndexOf(CONTEXT, trigger)) {
        toggleEventHandlers.onContextMenu = chainedFunction(
            handleClick,
            onContextMenu
        )
    }

    if (arrayIndexOf(HOVER, trigger)) {
        dropdownProps.onMouseEnter = chainedFunction(
            handleMouseEnter,
            onMouseEnter
        )
        dropdownProps.onMouseLeave = chainedFunction(
            handleMouseLeave,
            onMouseLeave
        )
    }

    const toggleElement = (
        <DropdownToggle
            {...rest}
            {...toggleEventHandlers}
            id={buttonId}
            ref={setTriggerRef} // Use the combined ref
            className={toggleClassName}
            renderTitle={renderTitle}
            disabled={disabled}
            placement={placement}
        >
            {title}
        </DropdownToggle>
    )

    // --- *** NEW LOGIC *** ---
    // We manually map over the children to attach our closing function
    // to their onClick prop.
    const menuItems = React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) {
            return child
        }
        return React.cloneElement(child, {
            onClick: chainedFunction(
                (e) => handleSelect(child.props.eventKey, e), // Our closing function
                child.props.onClick // The original onClick (e.g., opening the modal)
            ),
        })
    })
    // --- *** END NEW LOGIC *** ---

    const menuElement = (
        <DropdownMenu
            className={menuClass}
            // Apply positioning and z-index
            style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
                zIndex: 1000, // <-- ADDED Z-INDEX
                width: 'max-content',
                minWidth: 'auto',
                ...menuStyle, // Allow user styles to override
            }}
            // onSelect is no longer needed here, as we do it manually
            activeKey={activeKey}
            ref={setOverlayRef} // Use the combined ref
            hidden={!open}
            placement={placement}
            id={menuId}
        >
            {menuItems} {/* <-- Pass the new, cloned children */}
        </DropdownMenu>
    )

    return (
        <DropdownContext.Provider value={{ activeKey }}>
            <div
                {...dropdownProps}
                ref={ref}
                style={style}
                className="dropdown"
            >
                {toggleElement}
                <DropdownMenuContext.Provider value={menuControl}>
                    {/* Always render the menu in a portal */}
                    {createPortal(menuElement, document.body)}
                </DropdownMenuContext.Provider>
            </div>
        </DropdownContext.Provider>
    )
})

const {
    TOP_START,
    TOP_CENTER,
    TOP_END,
    BOTTOM_START,
    BOTTOM_CENTER,
    BOTTOM_END,
    MIDDLE_START_TOP,
    MIDDLE_START_BOTTOM,
    MIDDLE_END_TOP,
    MIDDLE_END_BOTTOM,
} = PLACEMENT

Dropdown.propTypes = {
    trigger: PropTypes.oneOf([CLICK, HOVER, CONTEXT]),
    placement: PropTypes.oneOf([
        TOP_START,
        TOP_CENTER,
        TOP_END,
        BOTTOM_START,
        BOTTOM_CENTER,
        BOTTOM_END,
        MIDDLE_START_TOP,
        MIDDLE_START_BOTTOM,
        MIDDLE_END_TOP,
        MIDDLE_END_BOTTOM,
    ]),
    menuClass: PropTypes.string,
    menuStyle: PropTypes.object,
    disabled: PropTypes.bool,
    title: PropTypes.string,
    renderTitle: PropTypes.node,
    activeKey: PropTypes.string,
    toggleClassName: PropTypes.string,
    onClick: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    onContextMenu: PropTypes.func,
    onSelect: PropTypes.func,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    onToggle: PropTypes.func,
    middleware: PropTypes.array,
}

Dropdown.defaultProps = {
    placement: BOTTOM_START,
    trigger: 'click',
    middleware: [offset(8), flip(), shift()],
}

export default Dropdown